import { Fragment } from "react";
import Marquee from "@/components/magicui/marquee";
import {
  useFetchTestimonials,
  useGetSocialProofAssetById,
} from "../api/queries";
import { GenericTestimonialCard } from "@/components/testify/card";
import { VariantId } from "@/components/testify/card/presets";
import { MarqueeGalleryCustomProps } from "../api/types";
import { WidgetComponent } from "@/types";
import { cn } from "@/lib/utils";
import { Show } from "@/components/atoms/show";
import { useIsMobile } from "@/hooks/use-mobile";
import { LoadFonts } from "@/components/atoms/load-fonts";

export interface Props {
  projectId: string;
  variant: VariantId;
  properties: MarqueeGalleryCustomProps;
}

export function VerticalMarqueeGallery({
  projectId,
  variant,
  properties,
}: Props) {
  const { data: testimonials, isPending } = useFetchTestimonials({
    projectId,
    published: true,
    type: "text",
  });
  const isMobile = useIsMobile();
  const firstRow = testimonials?.slice(0, testimonials?.length / 2);
  const secondRow = testimonials?.slice(testimonials?.length / 2);
  const scrollSpeed = properties?.scrollSpeed || 60;
  const isMultiColumnEnabled = Number(properties?.columnCount || 2) > 1;

  const shouldShowMultiColumn = isMultiColumnEnabled && !isMobile;

  return (
    <Fragment>
      {isPending ? (
        <div className="loader testify-absolute testify-top-1/2 testify-left-1/2 testify-transform -testify-translate-x-1/2 -testify-translate-y-1/2"></div>
      ) : (
        <div
          className="testify-container testify-mx-auto testify-w-full testify-max-w-[1000px] testify-max-h-screen testify-overflow-y-hidden testify-px-4 sm:testify-px-6 lg:testify-px-8"
          style={{ fontFamily: properties.font || "Roboto" }}
        >
          <LoadFonts font={properties.font || "Roboto"} />
          <div className="testify-relative testify-flex testify-h-full testify-flex-row testify-items-stretch testify-justify-center testify-gap-2 sm:testify-gap-4 testify-overflow-hidden testify-rounded-lg">
            <Marquee
              pauseOnHover
              vertical
              duration={Number(scrollSpeed)}
              className={cn("testify-h-full testify-min-h-full", {
                "testify-w-full": !shouldShowMultiColumn,
                "testify-w-1/2": shouldShowMultiColumn,
              })}
            >
              <div className="testify-flex testify-flex-col testify-gap-3 sm:testify-gap-5">
                {firstRow?.map((testimonial) => (
                  <GenericTestimonialCard
                    key={testimonial.id}
                    isUsedInParent
                    data={testimonial}
                    variant={variant}
                    wrapperStyles={{
                      maxWidth: isMobile ? "100%" : "450px",
                      height: "100%",
                      margin: "0",
                    }}
                    properties={properties}
                  />
                ))}
              </div>
            </Marquee>

            <Show when={shouldShowMultiColumn}>
              <Marquee
                reverse
                pauseOnHover
                vertical
                duration={Number(scrollSpeed)}
                className="testify-w-1/2 testify-min-h-full"
              >
                <div className="testify-flex testify-flex-col testify-gap-3 sm:testify-gap-5">
                  {secondRow?.map((testimonial) => (
                    <GenericTestimonialCard
                      key={testimonial.id}
                      isUsedInParent
                      data={testimonial}
                      variant={variant}
                      wrapperStyles={{
                        maxWidth: "450px",
                        height: "100%",
                        margin: "0",
                      }}
                      properties={properties}
                    />
                  ))}
                </div>
              </Marquee>
            </Show>

            <Show when={!properties?.fadeEdges}>
              <div className="testify-pointer-events-none testify-absolute testify-inset-x-0 testify-top-0 testify-h-8 sm:testify-h-12 testify-bg-gradient-to-b testify-from-white dark:testify-from-background testify-to-transparent"></div>
              <div className="testify-pointer-events-none testify-absolute testify-inset-x-0 testify-bottom-0 testify-h-8 sm:testify-h-12 testify-bg-gradient-to-t testify-from-white dark:testify-from-background testify-to-transparent"></div>
            </Show>
          </div>
        </div>
      )}
    </Fragment>
  );
}

export const VerticalMarqeeGalleryComponentWidget: WidgetComponent = ({
  widgetId,
}) => {
  const { data, isPending } =
    useGetSocialProofAssetById<MarqueeGalleryCustomProps>({ id: widgetId });

  if (!data || isPending) return;

  return (
    <VerticalMarqueeGallery
      projectId={data.projectId!}
      properties={data.properties}
      variant={data.properties.cardVariant}
    />
  );
};
